import React, { useCallback, useEffect, useRef, useState } from "react";
import { DeleteOutlined, FileTextOutlined, LockOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  InputNumber,
  Popconfirm,
  Segmented,
  Select,
  Tabs,
  Tooltip,
  message
} from "antd";
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import dayjs from "dayjs";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import {
  bulkFillNutritionItems,
  createNutritionTableItem,
  deleteNutritionTableItem,
  fetchAssignedGuests,
  fetchNutritionLookups,
  fetchNutritionMap,
  fetchNutritionTableItems,
  fetchNutritionTemplates,
  fetchUnassignedGuests,
  searchNutritionProducts,
  seatNutritionGuest,
  unseatNutritionGuest,
  updateNutritionTableItem
} from "./nutrition-api";
import {
  datesBetween,
  displayDate,
  emptyLookups,
  field,
  isoDate,
  lookupOptions,
  mealLabels,
  normalizeLookups,
  normalizeProduct,
  numberField,
  showError
} from "./nutrition-utils";
import NutritionDrawer, { DrawerFooter } from "./nutrition-drawer";
import "./nutrition.css";

const MAX_DAYS = 60;
const DOW = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const PART_SHORT = { 1: "З", 2: "О", 3: "П", 4: "У" };
const PRESET_DAYS = { week: 6, "2week": 13, month: 29 };

const presetOf = (period) => {
  if (!period?.[0]?.isSame(dayjs(), "day")) return null;
  const len = period[1].diff(period[0], "day");
  return Object.keys(PRESET_DAYS).find((key) => PRESET_DAYS[key] === len) || null;
};

const loadLevel = (count, max) => {
  if (!max) return count ? 2 : 1;
  if (!count) return 1;
  if (count >= max) return 4;
  return count >= max * 0.7 ? 3 : 2;
};

const TableRow = ({ table, days, cells, part, selected, onSelect, dndId }) => {
  const { setNodeRef, isOver } = useDroppable({ id: dndId });
  const tableId = numberField(table, "id");
  const max = numberField(table, "maxGuests");
  const today = isoDate(dayjs());
  return (
    <div ref={setNodeRef} className={`nutrition-map-row${isOver ? " nutrition-map-row--drop-over" : ""}`} style={{ "--days": days.length }}>
      <div className="nutrition-map-table">
        <div className="nutrition-map-table-name">
          {field(table, "name", "")}
          {numberField(table, "seatControl") === 1 && <LockOutlined title="Контроль мест" />}
        </div>
        <div className="nutrition-map-table-seats">{max ? `${max} мест` : "мест не ограничено"}</div>
      </div>
      {days.map((date) => {
        const iso = isoDate(date);
        const cell = cells.find((x) => numberField(x, "tableId") === tableId && isoDate(field(x, "fdate")) === iso);
        const isWeekend = [0, 6].includes(date.day());
        const active = selected?.tableId === tableId && selected?.date === iso;
        const tip = cell
          ? (part
              ? `${mealLabels[part]}: ${numberField(cell, `meal${part}`)} гостей`
              : [1, 2, 3, 4].map((p) => `${mealLabels[p]}: ${numberField(cell, `meal${p}`)}`).join(" · "))
          : "Нет данных";
        return (
          <Tooltip key={iso} title={tip}>
            <div
              role="button"
              tabIndex={-1}
              className={[
                "nutrition-map-cell",
                isWeekend ? "nutrition-map-cell--weekend" : "",
                iso === today ? "nutrition-map-cell--today" : "",
                active ? "nutrition-map-cell--selected" : ""
              ].filter(Boolean).join(" ")}
              onClick={() => onSelect(date, table)}
            >
              {part ? (
                <span className={`nutrition-map-count nutrition-map-count--l${loadLevel(numberField(cell, `meal${part}`), max)}`}>
                  {numberField(cell, `meal${part}`)}
                  {max ? <span className="nutrition-map-count-max">/{max}</span> : null}
                </span>
              ) : (
                <span className="nutrition-map-meals">
                  {[1, 2, 3, 4].map((p) => {
                    const count = numberField(cell, `meal${p}`);
                    return (
                      <span key={p} className={`nutrition-map-meal nutrition-map-meal--l${loadLevel(count, max)}`}>
                        {PART_SHORT[p]}<b>{count || "·"}</b>
                      </span>
                    );
                  })}
                </span>
              )}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

const GuestCard = ({ guest, draggable, onSeat, onUnseat }) => {
  const idDoc = numberField(guest, "idDoc");
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `guest-${idDoc}`,
    disabled: !draggable
  });
  const adults = Math.max(0, numberField(guest, "guestCount") - numberField(guest, "childCount"));
  const seat = numberField(guest, "tableSeat");
  return (
    <div
      ref={setNodeRef}
      {...(draggable ? { ...listeners, ...attributes } : {})}
      className={`nutrition-guest${draggable ? " nutrition-guest--drag" : ""}${isDragging ? " nutrition-guest--dragging" : ""}`}
    >
      <div className="nutrition-guest-top">
        <span className="nutrition-guest-name" title={field(guest, "predName", "")}>
          {field(guest, "guestName") || field(guest, "predName") || `Путёвка №${idDoc}`}
        </span>
        <Tooltip title={`Открыть путёвку №${idDoc}`}>
          <Link
            to={`/doc/exp/${idDoc}`}
            className="nutrition-guest-link"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <FileTextOutlined />
          </Link>
        </Tooltip>
        {draggable
          ? <Button type="primary" size="small" onClick={() => onSeat(guest)}>Посадить</Button>
          : <Button danger size="small" onClick={() => onUnseat(guest)}>Убрать</Button>}
      </div>
      <div className="nutrition-guest-meta">
        {field(guest, "numberName") && <span>{field(guest, "numberName")}</span>}
        {field(guest, "mealTypeName") && <span>{field(guest, "mealTypeName")}</span>}
        {draggable && <span>{adults} взр. · {numberField(guest, "childCount")} дет.</span>}
        {!draggable && seat > 0 && <span>место {seat}</span>}
        <span>{displayDate(field(guest, "dateBeg"))} — {displayDate(field(guest, "dateEnd"))}</span>
      </div>
      <div className="nutrition-guest-meals">
        {[1, 2, 3, 4].map((p) => (
          <span key={p} title={mealLabels[p]} className={`nutrition-guest-meal${numberField(guest, `meal${p}`) ? " nutrition-guest-meal--on" : ""}`}>
            {PART_SHORT[p]}
          </span>
        ))}
      </div>
    </div>
  );
};

const NutritionMap = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [itemForm] = Form.useForm();
  const [bulkForm] = Form.useForm();
  const [lookups, setLookups] = useState(emptyLookups);
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(() => {
    const beg = dayjs(searchParams.get("dateBeg"));
    const end = dayjs(searchParams.get("dateEnd"));
    return beg.isValid() && end.isValid() && !end.isBefore(beg) && end.diff(beg, "day") <= MAX_DAYS
      ? [beg.startOf("day"), end.startOf("day")]
      : [dayjs(), dayjs().add(6, "day")];
  });
  const [preset, setPreset] = useState(() => presetOf(period));

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("dateBeg", isoDate(period[0]));
    next.set("dateEnd", isoDate(period[1]));
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [period, searchParams, setSearchParams]);
  const [hallId, setHallId] = useState();
  const [part, setPart] = useState(0);
  const [map, setMap] = useState({ tables: [], cells: [] });
  const [unassigned, setUnassigned] = useState([]);
  const [selected, setSelected] = useState(null);
  const [assigned, setAssigned] = useState([]);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [itemEditor, setItemEditor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchSeq = useRef(0);
  const searchTimer = useRef(0);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    fetchNutritionLookups()
      .then((data) => {
        const value = normalizeLookups(data);
        setLookups(value);
        setHallId(value.halls[0]?.id);
      })
      .catch((e) => showError(messageApi, e, "Не удалось загрузить справочники"));
  }, [messageApi]);

  const load = useCallback(async () => {
    if (!hallId || !period?.[0] || !period?.[1]) return;
    setLoading(true);
    try {
      const [mapData, guestData] = await Promise.all([
        fetchNutritionMap(isoDate(period[0]), isoDate(period[1]), hallId),
        fetchUnassignedGuests(isoDate(period[0]), isoDate(period[1]))
      ]);
      setMap({
        tables: field(mapData, "tables", []) || [],
        cells: field(mapData, "cells", []) || []
      });
      setUnassigned(Array.isArray(guestData) ? guestData : []);
    } catch (error) {
      showError(messageApi, error, "Не удалось загрузить карту");
    } finally {
      setLoading(false);
    }
  }, [hallId, period, messageApi]);
  useEffect(() => void load(), [load]);

  const loadCell = useCallback(async (cell) => {
    if (!cell) return;
    try {
      const [guestData, itemData] = await Promise.all([
        fetchAssignedGuests(cell.date, cell.tableId),
        fetchNutritionTableItems(cell.date, cell.tableId)
      ]);
      setAssigned(Array.isArray(guestData) ? guestData : []);
      setItems(Array.isArray(itemData) ? itemData : []);
    } catch (error) {
      showError(messageApi, error, "Не удалось загрузить стол");
    }
  }, [messageApi]);

  const selectCell = (date, table) => {
    const next = {
      date: isoDate(date),
      tableId: numberField(table, "id"),
      tableName: field(table, "name", ""),
      maxGuests: numberField(table, "maxGuests")
    };
    setSelected(next);
    loadCell(next);
  };

  const refresh = async () => {
    await load();
    if (selected) await loadCell(selected);
  };

  const applyPreset = (key) => {
    setPreset(key);
    setPeriod([dayjs(), dayjs().add(PRESET_DAYS[key] ?? 6, "day")]);
  };

  const changePeriod = (value) => {
    if (!value?.[0] || !value?.[1]) return;
    if (value[1].diff(value[0], "day") > MAX_DAYS) {
      messageApi.warning(`Период ограничен ${MAX_DAYS} днями`);
      return;
    }
    setPreset(null);
    setPeriod(value);
  };

  useEffect(() => {
    setSelected(null);
    setAssigned([]);
    setItems([]);
  }, [hallId]);
  useEffect(() => {
    setSelected((current) => {
      const inRange = current && period?.[0] && period?.[1]
        && current.date >= isoDate(period[0]) && current.date <= isoDate(period[1]);
      if (current && !inRange) {
        setAssigned([]);
        setItems([]);
        return null;
      }
      return current;
    });
  }, [period]);

  const doSeat = async (guest, tableId) => {
    const target = tableId || selected?.tableId;
    if (!target) return;
    try {
      await seatNutritionGuest(numberField(guest, "idDoc"), target);
      messageApi.success("Гости размещены за столом");
      await refresh();
    } catch (error) {
      showError(messageApi, error, "Не удалось разместить гостей");
    }
  };
  const doUnseat = async (guest) => {
    try {
      await unseatNutritionGuest(numberField(guest, "idDoc"));
      await refresh();
    } catch (error) {
      showError(messageApi, error, "Не удалось снять размещение");
    }
  };
  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || !active) return;
    const idDoc = Number(String(active.id).replace("guest-", ""));
    const guest = unassigned.find((x) => numberField(x, "idDoc") === idDoc);
    if (guest) doSeat(guest, over.id);
  };

  const searchProducts = (query = "") => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      const seq = ++searchSeq.current;
      try {
        const data = await searchNutritionProducts(query);
        if (seq === searchSeq.current) {
          setProducts((Array.isArray(data) ? data : []).map(normalizeProduct).filter((x) => x.idTov));
        }
      } catch {
        if (seq === searchSeq.current) setProducts([]);
      }
    }, 300);
  };
  const openItem = (row, partId) => {
    const editing = row && numberField(row, "idStr") ? row : null;
    setItemEditor(editing || { part: partId });
    setProducts(editing ? [{ idTov: numberField(editing, "idTov"), name: field(editing, "tovName", "") }] : []);
    itemForm.setFieldsValue({
      part: editing ? numberField(editing, "part") : (partId || lookups.parts[0]?.id || 1),
      idTov: editing ? numberField(editing, "idTov") : undefined,
      qty: editing ? numberField(editing, "qty") : 1
    });
  };
  const saveItem = async () => {
    const values = await itemForm.validateFields();
    const body = { part: values.part, idTov: values.idTov, qty: values.qty };
    setSaving(true);
    try {
      if (numberField(itemEditor, "idStr")) {
        await updateNutritionTableItem(selected.date, selected.tableId, numberField(itemEditor, "idStr"), body);
      } else {
        await createNutritionTableItem(selected.date, selected.tableId, body);
      }
      setItemEditor(null);
      await loadCell(selected);
    } catch (error) {
      showError(messageApi, error, "Не удалось сохранить блюдо");
    } finally {
      setSaving(false);
    }
  };
  const saveQty = async (row, qty) => {
    if (!Number.isFinite(qty) || qty <= 0) {
      if (selected) await loadCell(selected);
      return;
    }
    if (qty === numberField(row, "qty")) return;
    try {
      await updateNutritionTableItem(selected.date, selected.tableId, numberField(row, "idStr"), {
        part: numberField(row, "part"),
        idTov: numberField(row, "idTov"),
        qty
      });
      await loadCell(selected);
    } catch (error) {
      showError(messageApi, error, "Не удалось сохранить количество");
    }
  };
  const deleteItem = async (row) => {
    try {
      await deleteNutritionTableItem(selected.date, selected.tableId, numberField(row, "idStr"));
      await loadCell(selected);
    } catch (error) {
      showError(messageApi, error, "Не удалось удалить блюдо");
    }
  };

  const openBulk = async () => {
    setBulkOpen(true);
    try {
      const data = await fetchNutritionTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(messageApi, error, "Не удалось загрузить шаблоны");
    }
    bulkForm.setFieldsValue({
      tableIds: selected ? [selected.tableId] : map.tables.map((x) => numberField(x, "id")),
      dates: selected ? [selected.date] : datesBetween(period[0], period[1]).map(isoDate),
      clearBefore: false,
      multiplyByGuests: false,
      addNonZeroOnly: true
    });
  };
  const saveBulk = async () => {
    const values = await bulkForm.validateFields();
    setSaving(true);
    try {
      const result = await bulkFillNutritionItems({
        templateId: values.templateId,
        tableIds: values.tableIds,
        dates: values.dates,
        clearBefore: Boolean(values.clearBefore),
        multiplyByGuests: Boolean(values.multiplyByGuests),
        addNonZeroOnly: Boolean(values.addNonZeroOnly)
      });
      messageApi.success(`Добавлено строк: ${numberField(result, "inserted")}`);
      setBulkOpen(false);
      await refresh();
    } catch (error) {
      showError(messageApi, error, "Не удалось заполнить меню");
    } finally {
      setSaving(false);
    }
  };

  const days = datesBetween(period?.[0], period?.[1]);
  const today = isoDate(dayjs());
  const seats = map.tables.reduce((s, x) => s + numberField(x, "maxGuests"), 0);
  const seatedToday = Math.max(0, ...[1, 2, 3, 4].map((p) =>
    map.cells
      .filter((x) => isoDate(field(x, "fdate")) === today)
      .reduce((s, x) => s + numberField(x, `meal${p}`), 0)));
  const dayTotal = (date, p) => map.cells
    .filter((x) => isoDate(field(x, "fdate")) === isoDate(date))
    .reduce((s, x) => s + numberField(x, `meal${p}`), 0);

  const itemsByPart = lookups.parts
    .map((p) => ({ part: p, rows: items.filter((x) => numberField(x, "part") === p.id) }));

  const sideBody = selected ? (
    <Tabs
      items={[
        {
          key: "assigned",
          label: `За столом (${assigned.length})`,
          children: assigned.length
            ? assigned.map((g) => <GuestCard key={`${numberField(g, "idDoc")}:${numberField(g, "idStr")}`} guest={g} onUnseat={doUnseat} />)
            : <div className="nutrition-empty">Нет размещённых гостей</div>
        },
        {
          key: "unassigned",
          label: `Не размещены (${unassigned.length})`,
          children: unassigned.length
            ? unassigned.map((g) => <GuestCard key={`${numberField(g, "idDoc")}:${numberField(g, "idStr")}`} guest={g} draggable onSeat={doSeat} />)
            : <div className="nutrition-empty">Все гости размещены</div>
        },
        {
          key: "items",
          label: `Меню (${items.length})`,
          children: itemsByPart.map(({ part: p, rows }) => (
            <div key={p.id} className="nutrition-menu-section">
              <div className="nutrition-menu-head">
                <span>{mealLabels[p.id] || p.name}</span>
                <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => openItem(null, p.id)} />
              </div>
              {rows.map((r) => (
                <div key={numberField(r, "idStr")} className="nutrition-menu-row">
                  <span className="nutrition-menu-name" title="Редактировать" onClick={() => openItem(r)}>{field(r, "tovName", "")}</span>
                  <InputNumber
                    size="small"
                    min={0.001}
                    precision={3}
                    defaultValue={numberField(r, "qty")}
                    style={{ width: 78 }}
                    onBlur={(e) => saveQty(r, Number(e.target.value))}
                    onPressEnter={(e) => saveQty(r, Number(e.target.value))}
                  />
                  <Popconfirm title="Удалить блюдо?" onConfirm={() => deleteItem(r)}>
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>
                </div>
              ))}
              {!rows.length && <div className="nutrition-guest-meta">Нет блюд</div>}
            </div>
          ))
        }
      ]}
    />
  ) : (
    <>
      <div className="nutrition-stats">
        <div className="nutrition-stat"><div className="nutrition-stat-value">{map.tables.length}</div><div className="nutrition-stat-label">столов</div></div>
        <div className="nutrition-stat"><div className="nutrition-stat-value">{seats}</div><div className="nutrition-stat-label">мест</div></div>
        <div className="nutrition-stat"><div className="nutrition-stat-value">{seatedToday}</div><div className="nutrition-stat-label">гостей сегодня</div></div>
        <div className="nutrition-stat"><div className="nutrition-stat-value">{unassigned.length}</div><div className="nutrition-stat-label">не размещены</div></div>
      </div>
      <div className="nutrition-hint">Выберите ячейку в сетке, чтобы увидеть гостей и меню стола. Перетащите неразмещённых гостей на строку стола.</div>
    </>
  );

  return (
    <div className="nutrition-page">
      {contextHolder}
      <div className="nutrition-toolbar">
        <h1 className="nutrition-title">Карта питания</h1>
        <Segmented value={hallId} options={lookupOptions(lookups.halls)} onChange={setHallId} />
        <Segmented
          value={preset}
          options={[
            { value: "week", label: "Неделя" },
            { value: "2week", label: "2 недели" },
            { value: "month", label: "Месяц" }
          ]}
          onChange={applyPreset}
        />
        <DatePicker.RangePicker value={period} format="DD.MM.YYYY" allowClear={false} onChange={changePeriod} />
        <Segmented
          value={part}
          options={[{ value: 0, label: "Все" }, ...lookupOptions(lookups.parts)]}
          onChange={setPart}
        />
        <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>Обновить</Button>
        <ReportButton
          vid={REPORT_VID.NUTRITION_MAP}
          id={hallId}
          begPer={period?.[0]}
          endPer={period?.[1]}
          part={part || undefined}
          disabled={!hallId}
        />
        <Button type="primary" onClick={openBulk}>Заполнить по шаблону</Button>
      </div>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="nutrition-map-body">
          <div className="nutrition-map-grid-wrap">
            <div className="nutrition-map-row nutrition-map-row--head" style={{ "--days": days.length }}>
              <div className="nutrition-map-corner">Стол</div>
              {days.map((date) => {
                const iso = isoDate(date);
                const isWeekend = [0, 6].includes(date.day());
                return (
                  <div key={iso} className={`nutrition-map-th${isWeekend ? " nutrition-map-th--weekend" : ""}${iso === today ? " nutrition-map-th--today" : ""}`}>
                    <span>{date.format("DD.MM")}</span>
                    <span className="nutrition-map-th-dow">{DOW[date.day()]}</span>
                  </div>
                );
              })}
            </div>
            {map.tables.map((table) => (
              <TableRow
                key={numberField(table, "id")}
                table={table}
                days={days}
                cells={map.cells}
                part={part}
                selected={selected}
                onSelect={selectCell}
                dndId={numberField(table, "id")}
              />
            ))}
            <div className="nutrition-map-row nutrition-map-row--total" style={{ "--days": days.length }}>
              <div className="nutrition-map-table"><div className="nutrition-map-table-name">Итого</div></div>
              {days.map((date) => (
                <div key={isoDate(date)} className="nutrition-map-cell">
                  {part ? (
                    dayTotal(date, part) || ""
                  ) : (
                    <span className="nutrition-map-meals">
                      {[1, 2, 3, 4].map((p) => {
                        const count = dayTotal(date, p);
                        return (
                          <span key={p} className={`nutrition-map-meal nutrition-map-meal--l${count ? 2 : 1}`}>
                            {PART_SHORT[p]}<b>{count || "·"}</b>
                          </span>
                        );
                      })}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="nutrition-side">
            <div className="nutrition-side-head">
              <div className="nutrition-side-title">
                {selected ? `${selected.tableName} · ${displayDate(selected.date)}` : "Сводка по залу"}
              </div>
              {selected && (
                <div className="nutrition-side-sub">
                  {selected.maxGuests ? `${selected.maxGuests} мест` : "Без ограничения мест"}
                </div>
              )}
            </div>
            <div className="nutrition-side-body">{sideBody}</div>
          </div>
        </div>
      </DndContext>
      <NutritionDrawer open={Boolean(itemEditor)} title={numberField(itemEditor, "idStr") ? "Блюдо стола" : "Добавить блюдо"} onClose={() => setItemEditor(null)}>
        <Form form={itemForm} layout="vertical">
          <Form.Item name="part" label="Приём пищи" rules={[{ required: true }]}><Select options={lookupOptions(lookups.parts)} /></Form.Item>
          <Form.Item name="idTov" label="Блюдо" rules={[{ required: true }]}>
            <Select showSearch filterOption={false} options={products.map((x) => ({ value: x.idTov, label: x.name }))} onFocus={() => searchProducts("")} onSearch={searchProducts} />
          </Form.Item>
          <Form.Item name="qty" label="Количество" rules={[{ required: true }]}><InputNumber min={0.001} precision={3} style={{ width: "100%" }} /></Form.Item>
          <DrawerFooter onSave={saveItem} onClose={() => setItemEditor(null)} saving={saving} />
        </Form>
      </NutritionDrawer>
      <NutritionDrawer open={bulkOpen} title="Заполнение меню по шаблону" onClose={() => setBulkOpen(false)} width={480}>
        <Form form={bulkForm} layout="vertical">
          <Form.Item name="templateId" label="Шаблон" rules={[{ required: true }]}><Select options={templates.map((x) => ({ value: numberField(x, "id"), label: field(x, "name", "") }))} /></Form.Item>
          <Form.Item name="tableIds" label="Столы" rules={[{ required: true }]}><Select mode="multiple" options={map.tables.map((x) => ({ value: numberField(x, "id"), label: field(x, "name", "") }))} /></Form.Item>
          <Form.Item name="dates" label="Даты" rules={[{ required: true }]}><Select mode="multiple" options={days.map((x) => ({ value: isoDate(x), label: displayDate(x) }))} /></Form.Item>
          <Form.Item name="clearBefore" valuePropName="checked"><Checkbox>Очистить существующие строки</Checkbox></Form.Item>
          <Form.Item name="multiplyByGuests" valuePropName="checked"><Checkbox>Умножать количество на гостей</Checkbox></Form.Item>
          <Form.Item name="addNonZeroOnly" valuePropName="checked"><Checkbox>Добавлять только ненулевые позиции</Checkbox></Form.Item>
          <DrawerFooter onSave={saveBulk} onClose={() => setBulkOpen(false)} saving={saving} saveText="Заполнить" />
        </Form>
      </NutritionDrawer>
    </div>
  );
};

export default NutritionMap;
